import { codeHighlightingLexicalTheme } from '@w/code-highlighting'
import inlineStyles from './inline-styles.module.css'

/**
 * Package-owned Tailwind skin. Every consumer gets identical editor geometry. Weights on bold leaves and headings are
 * plain `font-weight` declarations, not `font-bold`/`font-medium`: those utilities also set the registered
 * `--tw-font-weight` (`inherits: false`, read by no rule), and that custom property on every bold leaf cost 4.2 of 17.8 ms
 * of open's style recalculation at 2,000 blocks (static DOM probe; docs/benchmarks/sync/editor/README.md, Tried).
 */
export const blockEditorTheme = {
  paragraph:
    'cb-paragraph text-body-medium relative my-3 min-h-[23px] data-[cb-placeholder]:before:pointer-events-none data-[cb-placeholder]:before:absolute data-[cb-placeholder]:before:inset-0 data-[cb-placeholder]:before:whitespace-nowrap data-[cb-placeholder]:before:text-on-surface-secondary data-[cb-placeholder]:before:content-[attr(data-cb-placeholder)]',
  quote: 'cb-quote border-primary my-5 border-l-4 py-1 ps-4',
  heading: {
    h1: 'cb-h1 text-header-big mt-8 mb-3 [font-weight:var(--weight-semi)] text-balance first:mt-0',
    h2: 'cb-h2 text-header-medium mt-8 mb-3 [font-weight:var(--weight-semi)] text-balance first:mt-0',
    h3: 'cb-h3 text-header-small mt-8 mb-3 [font-weight:var(--weight-semi)] text-balance first:mt-0',
    h4: 'cb-h4 my-2 mt-4 text-lg font-semibold first:mt-1',
    h5: 'cb-h5 my-2 mt-4 text-base font-semibold first:mt-1',
    h6: 'cb-h6 my-2 mt-4 text-base font-semibold first:mt-1',
  },
  list: {
    nested: {
      // Lexical applies this class to every item containing a nested list,
      // including ordinary Markdown parents that also contain text. Hiding the
      // marker here therefore removes real parent bullets, not only synthetic
      // nesting containers.
      listitem: 'cb-nested-listitem',
    },
    ol: 'cb-list-ol marker:text-primary my-3 list-decimal ps-6',
    ul: 'cb-list-ul marker:text-primary my-3 list-disc ps-6',
    listitem: 'cb-listitem my-1',
    listitemChecked:
      'cb-listitem-checked text-on-surface-secondary relative my-px list-none ps-7 line-through',
    listitemUnchecked:
      'cb-listitem-unchecked relative my-px list-none ps-7',
  },
  // unni SimpleA: primary, bold/wide, underline animates in from the left on hover.
  link: 'cb-link text-primary relative inline-block cursor-pointer font-bold tracking-[1.3px] no-underline after:absolute after:bottom-[-2px] after:left-0 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-current after:transition-transform after:duration-200 after:ease-in-out after:content-[""] [@media(hover:hover)_and_(pointer:fine)]:hover:after:scale-x-100',
  text: {
    bold: 'cb-bold [font-weight:var(--weight-bold)]',
    italic: 'cb-italic italic',
    underline: 'cb-underline underline decoration-2 underline-offset-[5px]',
    strikethrough: 'cb-strikethrough line-through',
    code: `${inlineStyles.code} cb-inline-code whitespace-pre-wrap wrap-anywhere bg-gray/10 text-gray rounded-md px-[3px] py-px text-label-medium font-semi leading-none`,
  },
  code: 'cb-code group text-on-surface relative my-5 block min-w-0',
  codeHighlight: codeHighlightingLexicalTheme,
  tableScrollableWrapper: 'my-5 relative w-full max-w-full min-w-0 overflow-x-auto overflow-y-hidden rounded-[28px]',
  table: 'cb-table w-full caption-bottom text-sm',
  tableCellSelected: 'cb-table-cell-selected bg-primary/20',
  tableCell:
    'cb-table-cell relative min-w-28 p-4 align-middle leading-none data-[cb-placeholder]:before:pointer-events-none data-[cb-placeholder]:before:absolute data-[cb-placeholder]:before:inset-4 data-[cb-placeholder]:before:whitespace-nowrap data-[cb-placeholder]:before:text-on-surface-secondary data-[cb-placeholder]:before:content-[attr(data-cb-placeholder)] [&>p]:m-0 [&>p]:min-h-3.5 [&>p]:text-sm [&>p]:leading-none',
  tableCellHeader:
    'cb-table-cell-header bg-surface-2 relative min-w-28 p-4 align-middle leading-none font-semibold data-[cb-placeholder]:before:pointer-events-none data-[cb-placeholder]:before:absolute data-[cb-placeholder]:before:inset-4 data-[cb-placeholder]:before:whitespace-nowrap data-[cb-placeholder]:before:text-on-surface-secondary data-[cb-placeholder]:before:content-[attr(data-cb-placeholder)] [&>p]:m-0 [&>p]:min-h-3.5 [&>p]:text-sm [&>p]:leading-none',
  tableRow:
    'cb-table-row border-border border-b transition-colors last:border-b-0',
  hr: 'cb-hr border-border my-8 border-0 border-t data-[lexical-selected=true]:outline-2 data-[lexical-selected=true]:outline-primary',
}
